import { AbstractControl } from '@angular/forms';
import { Cmg1003LoanStore } from '../../loan-state';
import { Injector, effect, untracked, runInInjectionContext, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

export interface DateLogicConfig {
  closingControl: AbstractControl;
  fundingControl: AbstractControl;
  paymentControl: AbstractControl;
  daysOfPerDiemControl?: AbstractControl;
  loanStore: Cmg1003LoanStore;
  getLoanProgramCode: () => string;
}

export const formatDatePCalendar = (date?: Date | string) => (date ? new Date(date) : null);

export const dateLessThanNow = (dateStr: string): boolean => {
  if (!dateStr) {
    return false;
  }
  const date = strToDateUTC(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return date < now;
};

export const dateGreaterOrEqualThanNow = (dateStr: string): boolean => {
  if (!dateStr) {
    return false;
  }
  const date = strToDateUTC(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return date >= now;
};

export const strToDateUTC = (dateStr: string): Date => {
  const date = new Date(dateStr);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  date.setHours(0, 0, 0, 0);

  return date;
};

/**
 * Creates a new Date object with the same year, month, and day as the input date,
 * but with time set to 00:00:00 UTC (midnight). This removes any time component
 * and normalizes the date to UTC timezone.
 *
 * @param date The input date to strip time from (Date object or ISO string)
 * @returns A new Date object with time set to midnight UTC, or undefined if input is falsy or invalid
 */
export const dateWithoutTimeUTC = (date: Date | string): Date | undefined => {
  if (!date) {
    return;
  }

  let dateObj: Date;

  // Handle string input by converting to Date
  if (typeof date === 'string') {
    dateObj = new Date(date);
    // Validate the parsed date
    if (isNaN(dateObj.getTime())) {
      return;
    }
  } else {
    // Handle Date input
    if (isNaN(date.getTime())) {
      return;
    }
    dateObj = date;
  }

  // Create UTC date with time set to midnight
  return new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
};

/**
 * Formats a Date object or date string to ISO string format without milliseconds (YYYY-MM-DDTHH:mm:ss).
 * Supports both Date objects and date strings as input.
 *
 * @param date - The Date object or date string to format
 * @returns ISO string without milliseconds and timezone
 * @throws Error if the date is invalid
 * @example
 * formatDateWithoutMilliseconds(new Date()) // "2023-08-18T14:30:45"
 * formatDateWithoutMilliseconds("2023-08-18T14:30:45.123Z") // "2023-08-18T14:30:45"
 */
export const formatDateWithoutMilliseconds = (date: Date | string): string | undefined => {
  if (!date) {
    return;
  }

  let dateObj: Date;

  // Handle string input by converting to Date
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  // Validate the Date object
  if (isNaN(dateObj?.getTime())) {
    return;
  }

  // Convert to ISO string and remove milliseconds and timezone
  return dateObj.toISOString().slice(0, 19);
};

/**
 * Clear1 logic from the Cost Closing Worksheet whenever Estimated Closing Date (SchedClosingDate) is populated
 *
 * @param selectedDate This date is used to calculate the first payment date
 * @returns First day of month after selectedDate if the day is 1st of month, otherwise first day of month 2 months from selectedDate
 */
export const getFirstPaymentDate = (selectedDate: Date): Date => {
  if (selectedDate.getDate() === 1) {
    return new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate());
  } else {
    return new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 2, 1);
  }
};

/**
 * Calculates the First Payment Date based on the Loan Program Code and base date.
 * Matches logic from Closing Cost Worksheet (#90026).
 */
export function calculateFirstPaymentDate(baseDate: Date, loanProgramCode: string): Date {
  if (!baseDate) {
    return;
  }
  const code = loanProgramCode?.toUpperCase() || '';
  if (code.includes('AIO')) {
    const isTXHS = code.includes('TXHS');
    const aioMonthOffset = isTXHS ? 1 : 2;
    return new Date(baseDate.getFullYear(), baseDate.getMonth() + aioMonthOffset, 21);
  }

  const monthOffset = baseDate.getDate() === 1 ? 1 : 2;
  return new Date(baseDate.getFullYear(), baseDate.getMonth() + monthOffset, 1);
}

/**
 * Calculates the number of Per Diem days between Funding/Closing and First Payment.
 */
export function calculateDaysOfPerDiem(
  fundingDate: Date | null,
  closingDate: Date | null,
  firstPaymentDate: Date | null,
  loanProgramCode: string,
): number {
  if (!firstPaymentDate || (!fundingDate && !closingDate)) {
    return 0;
  }

  const code = loanProgramCode?.toUpperCase() || '';
  if (code.includes('AIO') && !code.includes('TXHS')) {
    return 0;
  }

  const dayOfMonth = code.includes('AIO') ? 21 : 1;

  const interestFrom = fundingDate ? new Date(fundingDate) : new Date(closingDate);
  const interestTo = new Date(firstPaymentDate.getFullYear(), firstPaymentDate.getMonth() - 1, dayOfMonth);

  interestFrom.setHours(0, 0, 0, 0);
  interestTo.setHours(0, 0, 0, 0);

  const oneDay = 1000 * 60 * 60 * 24;
  return Math.ceil((interestTo.getTime() - interestFrom.getTime()) / oneDay);
}

/**
 * Calculates the minimum allowable date for date pickers.
 * Defaults to Today (stripped of time), but includes existing saved dates to prevent invalidation.
 */
export function calculateMinDate(
  schedClosingDate: Date | string | null,
  schedFundingDate: Date | string | null,
  firstPaymentDate: Date | string | null,
): Date {
  const toMidnight = (val: Date | string): Date => {
    const d = new Date(val);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const today = toMidnight(new Date());
  const allDates: Date[] = [today];

  if (schedClosingDate) {
    allDates.push(toMidnight(schedClosingDate));
  }
  if (schedFundingDate) {
    allDates.push(toMidnight(schedFundingDate));
  }
  if (firstPaymentDate) {
    allDates.push(toMidnight(firstPaymentDate));
  }

  return allDates.reduce((min, current) => (current < min ? current : min));
}

const getDate = (v: any) => (v && !isNaN(new Date(v).getTime()) ? new Date(v) : null);
const getTime = (v: any) => getDate(v)?.getTime();

export function setupDateLogicSignals(config: DateLogicConfig, injector: Injector) {
  runInInjectionContext(injector, () => {
    const { closingControl, fundingControl, paymentControl, daysOfPerDiemControl, loanStore, getLoanProgramCode } = config;

    const closingDate = toSignal(closingControl.valueChanges, { initialValue: closingControl.value });
    const fundingDate = toSignal(fundingControl.valueChanges, { initialValue: fundingControl.value });
    const userPaymentInput = toSignal(paymentControl.valueChanges, { initialValue: paymentControl.value });

    const state = {
      fundingTime: getTime(fundingControl.value),
      closingTime: getTime(closingControl.value),
      userPaymentTime: getTime(paymentControl.value),
    };

    const activePaymentDate = signal(getDate(paymentControl.value));

    effect(
      () => {
        const userVal = getDate(userPaymentInput());
        const userTime = getTime(userVal);

        if (userTime !== state.userPaymentTime) {
          state.userPaymentTime = userTime;
          activePaymentDate.set(userVal);
          untracked(() => loanStore.update({ firstPaymentDate: userVal }));
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const funding = getDate(fundingDate());
        const closing = getDate(closingDate());
        const fundingTime = getTime(funding);
        const closingTime = getTime(closing);

        const hasChanged = fundingTime !== state.fundingTime || closingTime !== state.closingTime;

        state.fundingTime = fundingTime;
        state.closingTime = closingTime;

        if (!hasChanged) {
          return;
        }

        const baseDate = funding || closing;
        if (baseDate) {
          const code = untracked(() => getLoanProgramCode());
          const nextPayment = calculateFirstPaymentDate(baseDate, code);
          const nextPaymentTime = getTime(nextPayment);

          untracked(() => {
            if (getTime(activePaymentDate()) !== nextPaymentTime) {
              state.userPaymentTime = nextPaymentTime;

              activePaymentDate.set(nextPayment);
              paymentControl.setValue(nextPayment, { emitEvent: false });
              loanStore.update({ firstPaymentDate: nextPayment });
            }
          });
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const currentFunding = fundingDate();
      const currentClosing = closingDate();
      const currentPayment = activePaymentDate();

      const days = calculateDaysOfPerDiem(currentFunding, currentClosing, currentPayment, getLoanProgramCode());

      untracked(() => {
        if (daysOfPerDiemControl && daysOfPerDiemControl.value !== days) {
          daysOfPerDiemControl.setValue(days, { emitEvent: false });
        }

        loanStore.update({
          daysOfPerDiem: days,
          schedClosingDate: currentClosing,
          schedFundingDate: currentFunding,
        });
      });
    });
  });
}
